import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ChevronDown, Circle, Lock, PlayCircle } from 'lucide-react';
import type { CourseDetailResponse } from '@/api/types';
import { lessonPath } from '@/lib/structuredData';
import { cn } from '@/lib/cn';
import { formatClock, formatTotalDuration } from './duration';

interface Props {
  course: CourseDetailResponse;
  completed: Set<string>;
  /** Dars sahifasida — joriy dars ajratib ko'rsatiladi */
  currentLesson?: string;
  /** Yon panel (ixcham) yoki kurs sahifasidagi to'liq ro'yxat */
  compact?: boolean;
  /** Premium/ADMIN — premium modul darslarida qulf belgisi ko'rsatilmaydi */
  /** Kurs (yoki uni o'z ichiga olgan kasb) sotib olingan */
  hasAccess?: boolean;
}

/**
 * Kurs dasturi: modullar va darslar. Qulflangan (Premium) modul darslari ham ko'rinadi — qulf belgisi bilan.
 * Modul sarlavhasi bosilsa ochiladi/yopiladi. Yopilgan modul darslari HTML da qoladi (`hidden`) —
 * prerender/SEO o'zgarmaydi. Boshlang'ich holat faqat props'dan: hamma modul yopiq, faqat dars
 * sahifasida joriy dars turgan modul ochiq — server va klient HTML bir xil.
 */
export function CourseCurriculum({ course, completed, currentLesson, compact, hasAccess }: Props) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<Set<number>>(
    () =>
      new Set(
        course.modules.filter((m) => !currentLesson || !m.lessons.some((l) => l.slug === currentLesson)).map((m) => m.id),
      ),
  );

  const toggle = (id: number) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  let index = 0;

  return (
    <ol className={cn('space-y-4', compact && 'space-y-3')}>
      {course.modules.map((m) => {
        const open = !collapsed.has(m.id);
        const listId = `module-${m.id}-lessons`;
        const totalSec = m.lessons.reduce((sum, l) => sum + (l.durationSec ?? 0), 0);
        return (
          <li key={m.id} className={cn(!compact && 'card overflow-hidden')}>
            <h3 className={cn(!compact && open && 'border-b border-line')}>
              <button
                type="button"
                onClick={() => toggle(m.id)}
                aria-expanded={open}
                aria-controls={listId}
                className={cn(
                  'flex w-full items-start gap-3 text-left transition-colors hover:bg-surface',
                  compact ? 'mb-1.5 rounded-DEFAULT px-1 py-1' : 'px-5 py-4',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className={cn('block font-semibold', compact ? 'text-sm' : 'text-base')}>{m.title}</span>
                  {!compact && m.description && (
                    <span className="mt-1 block text-sm font-normal text-content-secondary">{m.description}</span>
                  )}
                  {!compact && (
                    <span className="mt-1 block text-xs font-normal text-content-muted">
                      {t('courses.moduleLessons', { count: m.lessons.length })}
                      {totalSec > 0 && ` · ${formatTotalDuration(totalSec, t)}`}
                    </span>
                  )}
                </span>
                <ChevronDown
                  aria-hidden
                  className={cn(
                    'mt-0.5 shrink-0 text-content-muted transition-transform duration-200',
                    compact ? 'h-4 w-4' : 'h-5 w-5',
                    open && 'rotate-180',
                  )}
                />
              </button>
            </h3>
            <ul id={listId} hidden={!open} className={cn(!compact && 'divide-y divide-line')}>
              {m.lessons.map((l) => {
                index += 1;
                const done = completed.has(l.slug);
                const current = l.slug === currentLesson;
                const locked = !l.free && !hasAccess;
                const Icon = done ? CheckCircle2 : locked ? Lock : current ? PlayCircle : Circle;
                return (
                  <li key={l.slug}>
                    <Link
                      to={lessonPath(course.slug, l.slug)}
                      aria-current={current ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-3 transition-colors hover:bg-surface',
                        compact ? 'rounded-DEFAULT px-2 py-1.5 text-sm' : 'px-5 py-3',
                        current && 'bg-surface font-medium text-content',
                      )}
                    >
                      <Icon
                        className={cn(
                          'h-4 w-4 shrink-0',
                          done ? 'text-[var(--easy)]' : current ? 'text-brand-600' : 'text-content-muted',
                        )}
                        aria-label={done ? t('courses.completed') : undefined}
                      />
                      <span className="w-6 shrink-0 text-xs tabular-nums text-content-muted">{index}.</span>
                      <span className="min-w-0 flex-1 truncate">{l.title}</span>
                      {/* Bepul/pullik — dars darajasida; kursni sotib olganga belgi shart emas */}
                      {!hasAccess && (
                        <span
                          className={cn(
                            'shrink-0 rounded px-1.5 py-0.5 text-[11px] font-semibold',
                            l.free ? 'bg-[var(--easy)]/15 text-[var(--easy)]' : 'bg-[var(--medium)]/15 text-[var(--medium)]',
                          )}
                        >
                          {l.free ? t('courses.free') : t('courses.paid')}
                        </span>
                      )}
                      {l.durationSec ? (
                        <span className="shrink-0 text-xs tabular-nums text-content-muted">{formatClock(l.durationSec)}</span>
                      ) : null}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </li>
        );
      })}
    </ol>
  );
}
